/* ============================================================
   sb-consent.js — Cookie-Banner (Eigenbau) und Einwilligungskern
   ============================================================
   Loest Consentik ab. Zwei Haelften in einer Datei: unten die UI
   (zwei Ebenen, Schalter, Fokus), oben ein Kern aus reinen
   Funktionen, der ohne DOM laeuft und in tools/sb-consent-test.js
   gegen die Testfaelle des Plans gefahren wird.

   ── Vertrag (verbindlich; der Hub im Repo mdma-tools-hub baut
      dagegen, Aenderungen hier sind Aenderungen dort) ──────────

   Cookie `sb_consent`
     Path=/; Secure; SameSite=Lax; Max-Age=15552000 (180 Tage).
     Domain=.super-bicycles.com NUR, wenn der Hostname
     `super-bicycles.com` ist oder darauf endet. Auf
     ride-super.myshopify.com und 7c3987-ed.myshopify.com bleibt
     das Attribut weg — .myshopify.com ist Public Suffix, ein
     Domain-Cookie darauf wird verworfen.
     Wert: encodeURIComponent(JSON.stringify(
       { v:1, a, m, p, t:"<ISO-8601 Z>", id:"<UUID v4>" }))
     a/m/p sind 0 oder 1 (Statistik, Marketing, Komfort).
     Fehlend, unlesbar, v != 1 oder falsche Typen => Banner.

   `id`
     NEUE UUID pro Entscheidung, keine Besucher-ID. Das Protokoll
     im Hub ist append-only, `id` ist der Primaerschluessel des
     Datensatzes; das Cookie haelt die id der aktuell gueltigen
     Entscheidung als Nachweis fuer den Besucher. Eine ueber 180
     Tage stabile ID waere eine Wiedererkennung, die der Zweck
     nicht braucht.

   Shopify spiegeln
     Shopify.loadFeatures([{name:'consent-tracking-api',
     version:'0.1'}]) und dann setTrackingConsent({analytics,
     marketing, preferences}). OHNE `sale_of_data`: Shopify
     dokumentiert den Data-Sale-Opt-out als eigenen,
     kundeninitiierten Flow, der unabhaengig durchgesetzt wird —
     ihn hier mitzusenden vermischt zwei Rechtsgrundlagen im
     Shopify-Log und ist fuer einen EU-Shop wirkungslos.
     Best effort mit 5-s-Timeout: Adblocker blocken
     consent-tracking-api.js, dann kommt nie ein Callback.

   Protokoll
     POST <hub>/api/v1/consent, JSON
     {id, v, a, m, p, t, source:"shop", locale, textVersion, prev?},
     mode:'cors', keepalive:true, kein credentials. Fehler sind
     still; mit ?sb_debug=1 zusaetzlich ein console.warn.
     `prev` = id der Entscheidung, die das Cookie VOR dem
     Ueberschreiben trug — die Kette alt -> neu im Protokoll ist
     der Nachweis nach Art. 7 Abs. 1 DSGVO. Nur wenn ein gueltiges
     Cookie (v:1) mit UUID-id da ist, sonst fehlt der Key. Der Hub
     prueft `prev` als UUID (zod) und weist bei einem Fehlschlag
     den GANZEN Datensatz ab — ein manipuliertes Cookie-id darf
     die neue Entscheidung nicht mit ins 400 reissen.

   Downgrade
     Wird eine Kategorie abgewaehlt, verfallen zusaetzlich zum
     Reload die bekannten First-Party-Cookies dieser Kategorie
     (Muster in EXPIRE_PATTERNS, Auswahl in cookiesToExpire).
     Der Reload entlaedt nur die Skripte — die Cookies stuenden
     sonst bis zu ihrem eigenen Ablauf weiter im Browser.
     HttpOnly-Cookies wie `_shopify_marketing` und
     `_shopify_analytics` sind per JS NICHT loeschbar; sie stehen
     nicht einmal in document.cookie. Der Versuch bliebe wirkungs-
     los und wird deshalb gar nicht erst unternommen — die raeumt
     Shopify selbst anhand des gespiegelten Einwilligungsstandes.

   ── textVersion ────────────────────────────────────────────
   Die Texte stehen in snippets/sb-consent-banner.liquid und in
   einer zweiten Kopie im Hub (src/lib/consent/texts.ts). Das ist
   bewusste Dopplung: Storefront-Locales werden nicht deployt
   (.shopifyignore), Rechtstexte muessen aber im ausgelieferten
   Code stehen. `textVersion` (Attribut data-text-version) haengt
   am Text — wer den Text wesentlich aendert, zaehlt sie hoch.
   Wer Kategorien aendert, zaehlt `v` hoch: das erzwingt
   Re-Consent, weil parse() dann null liefert.

   ── Konfiguration ──────────────────────────────────────────
   Im Browser aus den data-Attributen des Wurzelelements
   ([data-sb-consent]: data-hub-origin, data-locale,
   data-text-version). Fehlt das Markup, wird window.SBConsentConfig
   gelesen — diesen Weg nutzt der Node-Test, dort liefert
   document.querySelector immer null.

   ── Warum kein Dawn-trapFocus ──────────────────────────────
   Dawns trapFocus (assets/global.js) ist ein Singleton am
   document; Cart- und Menue-Drawer raeumen es beim Oeffnen und
   Schliessen weg. Ebene 1 ist ohnehin nicht modal
   (aria-modal="false"), Ebene 2 zykliert mit acht Zeilen
   eigenem keydown-Handler.
   ============================================================ */

(function () {
  'use strict';

  if (window.SBConsent) return;

  var COOKIE_NAME = 'sb_consent';
  var COOKIE_MAX_AGE = 15552000; // 180 Tage
  var COOKIE_DOMAIN = 'super-bicycles.com';
  var SCHEMA_VERSION = 1;
  var API_TIMEOUT = 5000; // Adblocker liefern nie einen Callback
  var RELOAD_GRACE = 1500; // Downgrade: so lange wartet der Reload auf den POST
  var COOKIE_RE = new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)');

  /* ══════════════════════════════════════════════════════════
     Kern — reine Funktionen, kein DOM. Exportiert als
     SBConsent._core fuer tools/sb-consent-test.js.
     ══════════════════════════════════════════════════════════ */

  function uuid() {
    var c = window.crypto || window.msCrypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    var b = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') {
      c.getRandomValues(b);
    } else {
      for (var i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40; // Version 4
    b[8] = (b[8] & 0x3f) | 0x80; // Variante 10xx
    var h = [];
    for (var j = 0; j < 16; j++) h.push((b[j] + 0x100).toString(16).slice(1));
    return (
      h.slice(0, 4).join('') + '-' +
      h.slice(4, 6).join('') + '-' +
      h.slice(6, 8).join('') + '-' +
      h.slice(8, 10).join('') + '-' +
      h.slice(10, 16).join('')
    );
  }

  function isFlag(x) {
    return x === 0 || x === 1;
  }

  // Liest den Satz aus einem Cookie-Header. Alles, was nicht exakt dem
  // Vertrag entspricht, ist null — dann zeigt der Banner wieder.
  function parse(cookieHeader) {
    var hit = String(cookieHeader == null ? '' : cookieHeader).match(COOKIE_RE);
    if (!hit) return null;
    var rec;
    try {
      rec = JSON.parse(decodeURIComponent(hit[1]));
    } catch (e) {
      return null;
    }
    if (!rec || typeof rec !== 'object') return null;
    if (rec.v !== SCHEMA_VERSION) return null;
    if (!isFlag(rec.a) || !isFlag(rec.m) || !isFlag(rec.p)) return null;
    if (typeof rec.t !== 'string' || !rec.t) return null;
    if (typeof rec.id !== 'string' || !rec.id) return null;
    return { v: rec.v, a: rec.a, m: rec.m, p: rec.p, t: rec.t, id: rec.id };
  }

  // Suffix-Pruefung, kein indexOf: "notsuper-bicycles.com" enthaelt den
  // String, ist aber eine fremde Domain.
  function ownDomain(hostname) {
    var h = String(hostname || '').toLowerCase();
    return h === COOKIE_DOMAIN || h.slice(-(COOKIE_DOMAIN.length + 1)) === '.' + COOKIE_DOMAIN;
  }

  function cookieString(rec, hostname) {
    var value = encodeURIComponent(
      JSON.stringify({ v: rec.v, a: rec.a, m: rec.m, p: rec.p, t: rec.t, id: rec.id })
    );
    var out = COOKIE_NAME + '=' + value +
      '; Path=/; Secure; SameSite=Lax; Max-Age=' + COOKIE_MAX_AGE;
    if (ownDomain(hostname)) out += '; Domain=.' + COOKIE_DOMAIN;
    return out;
  }

  function toShopify(rec) {
    return { analytics: rec.a === 1, marketing: rec.m === 1, preferences: rec.p === 1 };
  }

  // Exakt die Form, die der Hub mit zod `z.uuid()` annimmt (RFC 9562:
  // Versionsziffer 1-8, Variante 8/9/a/b, dazu Nil- und Max-UUID). parse()
  // prueft die id nur auf "nicht leer" — fuer das Cookie reicht das, fuer
  // `prev` nicht, weil ein Fehlschlag dort den ganzen POST kostet.
  var UUID_RE = /^([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;

  function isUuid(x) {
    return typeof x === 'string' && UUID_RE.test(x);
  }

  function hubPayload(rec, loc, version, prevId) {
    var out = {
      id: rec.id,
      v: rec.v,
      a: rec.a,
      m: rec.m,
      p: rec.p,
      t: rec.t,
      source: 'shop',
      locale: loc,
      textVersion: version
    };
    if (isUuid(prevId)) out.prev = prevId;
    return out;
  }

  function makeRecord(a, m, p) {
    return {
      v: SCHEMA_VERSION,
      a: a ? 1 : 0,
      m: m ? 1 : 0,
      p: p ? 1 : 0,
      t: new Date().toISOString(),
      id: uuid()
    };
  }

  // Nur ein Downgrade rechtfertigt den Reload: eine Kategorie war an und ist
  // jetzt aus. Chat-Widget und Pixel laden sich nicht von selbst wieder aus.
  function isDowngrade(prev, next) {
    if (!prev) return false;
    return (
      (prev.a === 1 && next.a === 0) ||
      (prev.m === 1 && next.m === 0) ||
      (prev.p === 1 && next.p === 0)
    );
  }

  // Bekannte First-Party-Cookies je Kategorie. Muster statt Namensliste:
  // `_ga_<Property-ID>` und `_gcl_<Variante>` tragen einen variablen Teil,
  // eine feste Liste traefe sie nie. Umgekehrt sind die Muster bewusst eng
  // verankert (`^`), damit `_gaadsense` oder `_fbpixel-fremd` nicht
  // mitgeloescht werden.
  var EXPIRE_PATTERNS = {
    a: [/^_ga$/, /^_ga_/, /^_gid$/, /^_gat/],
    m: [/^_fbp$/, /^_fbc$/, /^_gcl_/, /^_pin_unauth$/],
    p: [/^sib_/, /^brevo/i]
  };

  // Namen aus einem Cookie-Header, ohne Werte und ohne Dubletten.
  function cookieNames(cookieHeader) {
    var parts = String(cookieHeader == null ? '' : cookieHeader).split(';');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      var name = (eq > -1 ? parts[i].slice(0, eq) : parts[i]).trim();
      if (name && out.indexOf(name) === -1) out.push(name);
    }
    return out;
  }

  // Welche Cookies verfallen beim Downgrade? Rein, ohne DOM — der Header
  // kommt von aussen, damit der Fall im Node-Harness ohne Browser laeuft.
  // Nur Kategorien, die vorher an waren und jetzt aus sind; nur Namen, die
  // im Header wirklich stehen.
  function cookiesToExpire(prev, next, cookieHeader) {
    var out = [];
    if (!prev || !next) return out;
    var names = cookieNames(cookieHeader);
    var cats = ['a', 'm', 'p'];
    for (var c = 0; c < cats.length; c++) {
      var key = cats[c];
      if (prev[key] !== 1 || next[key] !== 0) continue;
      var pats = EXPIRE_PATTERNS[key];
      for (var n = 0; n < names.length; n++) {
        if (names[n] === COOKIE_NAME) continue;
        for (var q = 0; q < pats.length; q++) {
          if (pats[q].test(names[n])) {
            if (out.indexOf(names[n]) === -1) out.push(names[n]);
            break;
          }
        }
      }
    }
    return out;
  }

  /* ══════════════════════════════════════════════════════════
     Konfiguration und Zustand
     ══════════════════════════════════════════════════════════ */

  var root = document.querySelector('[data-sb-consent]');
  var cfg = (root && root.dataset) || window.SBConsentConfig || {};
  var hub = String(cfg.hubOrigin || '').replace(/\/+$/, '');
  var locale = String(cfg.locale || 'de');
  var textVersion = String(cfg.textVersion || '');
  var debug = /(^|[?&])sb_debug=1(&|$)/.test(String(location.search || ''));
  // Der Theme-Editor laeuft im cross-site iframe, ein Lax-Cookie ist dort
  // unsichtbar. Banner anzeigen, aber nichts schreiben, spiegeln, senden.
  var dryRun = !!(window.Shopify && window.Shopify.designMode);

  var state = {
    rec: null,
    autoShow: false,
    level: 1,
    trigger: null
  };

  /* ══════════════════════════════════════════════════════════
     Shopify Customer Privacy
     ══════════════════════════════════════════════════════════ */

  var apiPromise = null;

  function api() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve(null);
      }, API_TIMEOUT);
      function done(value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      }
      try {
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
          done(null);
          return;
        }
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) {
            if (err) {
              done(null);
              return;
            }
            done(window.Shopify.customerPrivacy || null);
          }
        );
      } catch (e) {
        done(null);
      }
    });
    return apiPromise;
  }

  function mirror(rec) {
    return api().then(function (cp) {
      if (!cp || typeof cp.setTrackingConsent !== 'function') return;
      return new Promise(function (resolve) {
        var settled = false;
        var timer = setTimeout(function () {
          if (settled) return;
          settled = true;
          resolve();
        }, API_TIMEOUT);
        function done() {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve();
        }
        try {
          cp.setTrackingConsent(toShopify(rec), done);
        } catch (e) {
          done();
        }
      });
    });
  }

  // Beim Seitenaufruf mit gueltigem Cookie nur spiegeln, wenn Shopify etwas
  // anderes gespeichert hat. Sonst ein Log-Eintrag und ein Pixel-Re-Init pro
  // Seitenaufruf, ohne dass sich etwas geaendert haette.
  function mirrorIfMismatch(rec) {
    return api().then(function (cp) {
      if (!cp || typeof cp.setTrackingConsent !== 'function') return;
      var want = toShopify(rec);
      var cur = null;
      try {
        if (typeof cp.currentVisitorConsent === 'function') cur = cp.currentVisitorConsent();
      } catch (e) {
        cur = null;
      }
      if (
        cur &&
        (cur.analytics === 'yes') === want.analytics &&
        (cur.marketing === 'yes') === want.marketing &&
        (cur.preferences === 'yes') === want.preferences
      ) {
        return;
      }
      return mirror(rec);
    });
  }

  /* ══════════════════════════════════════════════════════════
     Protokoll
     ══════════════════════════════════════════════════════════ */

  function note(err) {
    if (debug && window.console && console.warn) {
      console.warn('[sb-consent] Protokoll fehlgeschlagen', err);
    }
  }

  function log(rec, prevId) {
    if (!hub || typeof window.fetch !== 'function') return Promise.resolve();
    var body;
    try {
      body = JSON.stringify(hubPayload(rec, locale, textVersion, prevId));
    } catch (e) {
      return Promise.resolve();
    }
    try {
      return Promise.resolve(
        window.fetch(hub + '/api/v1/consent', {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: { 'content-type': 'application/json' },
          body: body
        })
      ).then(null, note);
    } catch (e) {
      note(e);
      return Promise.resolve();
    }
  }

  /* ══════════════════════════════════════════════════════════
     Event
     ══════════════════════════════════════════════════════════ */

  function emit(rec) {
    var ev;
    try {
      ev = new window.CustomEvent('sb:consent', { detail: rec });
    } catch (e) {
      if (!document.createEvent) return;
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('sb:consent', false, false, rec);
    }
    document.dispatchEvent(ev);
  }

  /* ══════════════════════════════════════════════════════════
     Entscheidung
     ══════════════════════════════════════════════════════════ */

  // Ein Cookie verfaellt nur, wenn Name, Domain UND Path uebereinstimmen —
  // und keines der drei steht in document.cookie. Deshalb blind alle
  // Varianten schreiben, die wir selbst ausliefern koennten: host-only und,
  // auf eigener Domain, beide Schreibweisen des Domain-Attributs. Ein
  // Fehlschlag ist folgenlos, ein fehlender Treffer laesst das Cookie stehen.
  function expireCookie(name, hostname) {
    var base = name + '=; Max-Age=0; Path=/';
    var variants = [base];
    if (ownDomain(hostname)) {
      variants.push(base + '; Domain=.' + COOKIE_DOMAIN);
      variants.push(base + '; Domain=' + COOKIE_DOMAIN);
    }
    for (var i = 0; i < variants.length; i++) {
      try {
        document.cookie = variants[i];
      } catch (e) {
        /* Privater Modus ohne Cookie-Zugriff */
      }
    }
    return variants;
  }

  function decide(a, m, p) {
    var prev = state.rec;
    var rec = makeRecord(a, m, p);
    var down = isDowngrade(prev, rec);

    if (dryRun) {
      state.rec = rec;
      close();
      emit(rec);
      return Promise.resolve(rec);
    }

    // Vorgaenger fuer das Protokoll: das Cookie, BEVOR es ueberschrieben
    // wird. Bewusst nicht state.rec — hat ein zweiter Tab inzwischen
    // entschieden, traegt nur das Cookie dessen id, und die Kette liefe an
    // ihm vorbei. Kein gueltiges Cookie (fehlt, v != 1): kein prev.
    var before = null;
    try {
      before = parse(document.cookie);
    } catch (e) {
      /* Cookie-Zugriff verweigert: keine Kette */
    }

    try {
      document.cookie = cookieString(rec, location.hostname);
    } catch (e) {
      /* Privater Modus ohne Cookie-Zugriff: UI trotzdem schliessen. */
    }
    state.rec = rec;

    // Abgewaehlte Kategorien: die bekannten Cookies verfallen lassen, bevor
    // der Reload laeuft. Gelesen wird der Header NACH dem eigenen Schreiben —
    // sb_consent steht dann schon drin und wird von cookiesToExpire
    // uebergangen, alle uebrigen Cookies sind unveraendert da.
    if (down) {
      var stale = cookiesToExpire(prev, rec, document.cookie);
      for (var i = 0; i < stale.length; i++) expireCookie(stale[i], location.hostname);
    }

    var settled = Promise.all([mirror(rec), log(rec, before && before.id)]);
    close();
    emit(rec);

    if (!down) {
      return settled.then(function () {
        return rec;
      });
    }

    // Der Reload entlaedt Chat-Widget und Pixel wirklich. Er wartet auf den
    // POST, aber hoechstens RELOAD_GRACE — sonst haengt die Seite an einem
    // Hub, den ein Adblocker gerade wegfiltert.
    return new Promise(function (resolve) {
      var fired = false;
      var timer = setTimeout(finish, RELOAD_GRACE);
      function finish() {
        if (fired) return;
        fired = true;
        clearTimeout(timer);
        try {
          location.reload();
        } catch (e) {
          /* ignorieren */
        }
        resolve(rec);
      }
      settled.then(finish, finish);
    });
  }

  /* ══════════════════════════════════════════════════════════
     UI
     ══════════════════════════════════════════════════════════ */

  var el = {};

  function pick(name) {
    return root ? root.querySelector('[data-sbcs="' + name + '"]') : null;
  }

  function all(name) {
    return root ? root.querySelectorAll('[data-sbcs="' + name + '"]') : [];
  }

  function switches() {
    return root ? root.querySelectorAll('[role="switch"][data-cat]') : [];
  }

  function on(nodes, fn) {
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].addEventListener('click', fn);
    }
  }

  function raf(fn) {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  }

  function syncSwitches(rec) {
    var list = switches();
    for (var i = 0; i < list.length; i++) {
      var cat = list[i].getAttribute('data-cat');
      if (cat === 'n') {
        list[i].setAttribute('aria-checked', 'true');
        continue;
      }
      list[i].setAttribute('aria-checked', rec && rec[cat] === 1 ? 'true' : 'false');
    }
  }

  function readSwitches() {
    var out = { a: 0, m: 0, p: 0 };
    var list = switches();
    for (var i = 0; i < list.length; i++) {
      var cat = list[i].getAttribute('data-cat');
      if (cat === 'n' || !(cat in out)) continue;
      out[cat] = list[i].getAttribute('aria-checked') === 'true' ? 1 : 0;
    }
    return out;
  }

  function setLevel(n) {
    if (!root) return;
    state.level = n;
    if (n === 2) root.classList.add('is-level-2');
    else root.classList.remove('is-level-2');
    if (el.level1) el.level1.hidden = n === 2;
    if (el.level2) el.level2.hidden = n !== 2;
    root.setAttribute('aria-labelledby', n === 2 ? 'sbcs-title-2' : 'sbcs-title');
    root.setAttribute('aria-describedby', n === 2 ? 'sbcs-desc-2' : 'sbcs-desc');
    for (var i = 0; i < el.settings.length; i++) {
      el.settings[i].setAttribute('aria-expanded', n === 2 ? 'true' : 'false');
    }
    // Der Schliessen-Knopf darf nur wirken, wenn es schon eine Entscheidung
    // gibt — sonst waere er eine stille Ablehnung.
    if (el.close) el.close.hidden = !state.rec;
  }

  function firstSwitch() {
    var list = switches();
    for (var i = 0; i < list.length; i++) {
      if (!list[i].disabled) return list[i];
    }
    return null;
  }

  function show(level, focusSwitch) {
    if (!root) return;
    syncSwitches(state.rec);
    setLevel(level);
    root.hidden = false;
    // Erst sichtbar machen, im naechsten Frame die Klasse — sonst springt die
    // Einblend-Transition nicht an (FOUC bzw. gar keine Animation).
    raf(function () {
      root.classList.add('is-open');
    });
    var target = (focusSwitch && firstSwitch()) || root;
    try {
      target.focus();
    } catch (e) {
      /* ignorieren */
    }
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.hidden = true;
    var back = state.trigger;
    state.trigger = null;
    if (back && typeof back.focus === 'function') {
      try {
        back.focus();
      } catch (e) {
        /* ignorieren */
      }
    }
  }

  function open() {
    if (!root) return;
    show(2, true);
  }

  // Tab-Zyklus nur in Ebene 2. Ebene 1 ist bewusst nicht modal: der Besucher
  // muss die Seite lesen koennen, bevor er entscheidet.
  function focusables() {
    var out = [];
    if (!root) return out;
    var nodes = root.querySelectorAll(
      'a[href], button:not([disabled]), summary, input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.hidden) continue;
      if (n.closest && n.closest('[hidden]')) continue;
      out.push(n);
    }
    return out;
  }

  function cycle(e) {
    var list = focusables();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeyDown(e) {
    var key = e.key;
    if (key === 'Escape' || key === 'Esc') {
      // Dawns onKeyUpEscape schliesst offene <details>. Der eigene Handler
      // muss dort aussteigen, sonst verschwindet mit der Cookie-Liste gleich
      // die ganze Ebene.
      if (e.target && e.target.closest && e.target.closest('details[open]')) return;
      if (state.level === 2) {
        e.stopPropagation();
        if (state.rec) {
          close();
        } else {
          setLevel(1);
          try {
            root.focus();
          } catch (err) {
            /* ignorieren */
          }
        }
        return;
      }
      // Ebene 1 ohne Entscheidung: Escape tut nichts. Ein stilles Wegdruecken
      // waere keine Ablehnung und liesse die Seite ohne Entscheidung zurueck.
      if (state.rec) {
        e.stopPropagation();
        close();
      }
      return;
    }
    if (key === 'Tab' && state.level === 2) cycle(e);
  }

  function bind() {
    if (!root) return;
    el.level1 = pick('level-1');
    el.level2 = pick('level-2');
    el.close = pick('close');
    el.settings = all('settings');

    on(all('accept-all'), function () {
      decide(1, 1, 1);
    });
    on(all('reject-all'), function () {
      decide(0, 0, 0);
    });
    on(all('save'), function () {
      var picked = readSwitches();
      decide(picked.a, picked.m, picked.p);
    });
    on(el.settings, function () {
      setLevel(2);
      var s = firstSwitch();
      if (s) s.focus();
    });
    if (el.close) {
      el.close.addEventListener('click', function () {
        if (state.rec) close();
      });
    }

    var list = switches();
    for (var i = 0; i < list.length; i++) {
      if (list[i].disabled || list[i].getAttribute('data-cat') === 'n') continue;
      list[i].addEventListener('click', function () {
        this.setAttribute(
          'aria-checked',
          this.getAttribute('aria-checked') === 'true' ? 'false' : 'true'
        );
      });
    }

    root.addEventListener('keydown', onKeyDown);
  }

  /* ══════════════════════════════════════════════════════════
     Start
     ══════════════════════════════════════════════════════════ */

  function init() {
    state.rec = parse(document.cookie);
    bind();

    if (state.rec) {
      emit(state.rec);
      if (!dryRun) mirrorIfMismatch(state.rec);
    } else {
      state.autoShow = true;
      show(1, false);
    }

    // Der Link in der Rechtsleiste und jeder andere Link auf den Anker
    // oeffnen Ebene 2. Delegation, damit auch Links aus dem
    // Theme-Editor-Footer (custom-liquid) ohne eigenes JS funktionieren.
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href$="#cookie-einstellungen"]') : null;
      if (!a) return;
      e.preventDefault();
      state.trigger = a;
      open();
    });

    if (String(location.hash || '') === '#cookie-einstellungen') open();
  }

  window.SBConsent = {
    open: open,
    get: function () {
      return state.rec;
    },
    _core: {
      parse: parse,
      cookieString: cookieString,
      toShopify: toShopify,
      hubPayload: hubPayload,
      isUuid: isUuid,
      uuid: uuid,
      record: makeRecord,
      isDowngrade: isDowngrade,
      ownDomain: ownDomain,
      cookiesToExpire: cookiesToExpire
    },
    _decide: decide,
    _state: state
  };

  init();
})();
